import { createCrmApi } from 'services/crm/api'
import { CollapsingAction } from './collapsing-action'
import { PageCheck, slugify as backendSlug } from '../../lead-forms/api'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
	AppButton,
	DotMatrix,
	Input,
	Modal,
	NavItem,
	NavSection,
	TaskCheck,
	Toggle,
	Tooltip,
	useEnterExit,
	usePaintedScrollbar
} from '@clickguard/design-system'
import MinusCircle from '@untitled-ui/icons-react/build/esm/MinusCircle'
import PlayCircle from '@untitled-ui/icons-react/build/esm/PlayCircle'
import {
	BlockThreshold,
	BotLevel,
	BotSensitivity,
	CaptchaTrigger,
	CrmAccount,
	CrmConnections,
	CrmProvider,
	NewFormDraft,
	botLevelPatch,
	emailDefaults,
	useFormSpam,
	useSwapKey
} from '../form-spam-data'
import {
	DialogHeader,
	RequestSourceDialog,
	RequestState,
	useCrmConnect,
	useCrmField,
	useWidgetCheck
} from './form-settings-shared'
import { BotPane, CARDS, CrmPane, DetailsPane, DiscardDraftDialog, EmailPane, SubKey, slugify, useUrlVerify } from './form-setup-panes'
import { FORM_SPAM_TUTORIAL_URL } from './form-spam-refunds-agent-tokens'
import '../form-spam-page.css'

/**
 * R235 (Gustavo pin #65, 3 Sep): the link modal is a STEPPER.
 *
 * R182 built this file as a card screen: three checkable cards in the rail,
 * open in any order. Gustavo read that as three settings pages with no route
 * through them, and his pin asks for a wizard: the form first, then the three
 * protections one after the other, with the rail on the left as the map. So
 * the rail is now a stepper and the pane walks pages 0..3. The R182 shell (the
 * DS `Modal`, the 300px rail, the frosted scrollport, the painted thumb, the
 * button bar, the pane crossfade) is untouched; what sits INSIDE the rail and
 * the pane, and the footer logic, is new.
 *
 * Pages: 0 Form details, 1 Protect my ads, 2 Flag invalid submissions on my
 * CRM, 3 Block bot submissions. Each protection page carries its master switch
 * at the top right of the pane header. Switch OFF dims the page, disables Next
 * and grows a Skip button; the rail's lead mark turns to a minus circle. Next with
 * the switch ON prints "Done". R243 (Esteban, 3 Sep): with the CRM switch ON,
 * Next and Link form also wait until a CRM is connected and "Check my CRM"
 * found the spam-flag field; until then Next is disabled and no Skip grows.
 * R286 (Esteban, 7 Sep): with the bot switch ON, Link form waits the same way
 * for "Verify" to find the widget, and the disabled button carries a
 * tooltip that says so.
 *
 * Two assumptions, both open for Gustavo:
 *   1. The bot substep is the last page, so its switch OFF has no page to skip
 *      to. The footer keeps "Finish" (R321, once "Link form") enabled and the form links with bot
 *      verification off.
 *   2. Each substep header reuses the R182 card title as its title and the
 *      card subtitle as its subtitle, verbatim, so the copy he already read
 *      travels to the new layout unchanged.
 *
 * Data-model gap, flagged: the store holds ONE bot level and ONE trigger. The
 * bot page shows two independent switches (CAPTCHA, block). Both on stores
 * `block` + `high-only`; the CAPTCHA-for-suspects half of that pair has no
 * home in `NewFormDraft` yet.
 *
 * DS SWEEP (`cg-ds-inventory`, 3 Sep): the inventory has no vertical stepper.
 * R235 drew one from tokens (24px circles, a 2px rail) and Esteban read it as
 * "real real ugly". R236 composes the rail from the DS sidebar pattern the
 * settings page already uses: `NavSection` per step, `NavItem` per row, the
 * onboarding checklist's `TaskCheck` as the lead mark (R250: a minus circle for Off).
 * Composed from the DS besides those: `Modal`, `Toggle`, `Input`, `AppButton`,
 * `StatusDot`, `MenuList`, `ListItem`, `Divider`, `DotMatrix`, `useEnterExit`,
 * `usePopoverMotion`, `useAnchoredPlacement`, `usePaintedScrollbar`. Composed from the lane:
 * `EmailStep`, `CrmRow`, `CrmFieldPanel`, `ToggleRow`,
 * `WidgetStep`, `SettingsCard`, both dialogs and their hooks. New here: the
 * stepper rail, the connected-CRM card with its menu, the two-switch bot page.
 *
 * The old numbered wizard (`link-form-modal.tsx`) stays on disk untouched.
 * `form-spam-page.tsx` picks between the two with one constant.
 *
 * R262 (Esteban, 3 Sep): the four pages' CONTENT moved to `form-setup-panes.tsx`
 * (`DetailsPane`, `EmailPane`, `CrmPane`, `BotPane`) so the drawer's Setup tab
 * draws the same panes over the record. This file keeps the draft state, the
 * rail, the footer and the page walk, and hands each pane its slice.
 */
/**
 * R186: the frosted band's mask. It holds the glass solid for the first 28px,
 * the depth the close button needs, then takes both the tint and the blur to
 * nothing by 56px. Without it the band would end in a hard line across the
 * pane, which reads as a bug, not as glass.
 */
/* R189: how far the pane must scroll before the glass reaches full strength.
   Short on purpose. The band has to answer the scroll, not trail it. */
const GLASS_RAMP = 24

const MASK_TOP = 'linear-gradient(to bottom, #000 0px, #000 28px, rgba(0, 0, 0, 0) 56px)'

/* R203 (Esteban, 1 Sep): "I like the blur here but it's too damn tall".
   The bottom band is now 36px, not 56px, and it holds solid for only 14px.
   The top band keeps its 56px, because its solid core is what makes the
   close button legible over the scrolling rows. The bottom band backs
   nothing, so it only has to hint that the run goes on. */
const MASK_BOTTOM = 'linear-gradient(to top, #000 0px, #000 14px, rgba(0, 0, 0, 0) 36px)'

interface LinkFormCardsModalProps {
	open: boolean
	onClose: () => void
	onLink: (draft: NewFormDraft) => void | Promise<void>
	/** Accepted so the two screens take the same props. The stepper has no
	    first-run intro, so it does not read this. */
	forceFirstRun?: boolean
}

type PageKey = 'details' | SubKey
type Page = 0 | 1 | 2 | 3
/* R245 (Esteban, 3 Sep): "actually make the substep texts bigger too". The
   rows print at the DS `NavItem` sm size (14px/18px); the 12px override the
   settings nav applies (R236) is gone. At 14px the longest row ("Flag invalid
   submissions on my CRM", his verbatim copy) needs 233px beside the Off badge
   and the 300px rail gave it 181px, so the rail is 360px and the modal 860px:
   the right pane keeps its 500px. */
/* R250 (Esteban, 3 Sep): "left pane less wide man !!!!!". The Off badge in the
   trailing slot set a 352px floor (233px label + 119px furniture). The Off
   state now lives in the lead mark (`RailMark`: a minus circle in the
   TaskCheck's 16px box), the trailing slot is empty, and the longest row needs
   309px: the rail is 320px and the modal 820px; the right pane keeps 500px. */
/* R244 (Esteban, 3 Sep): "Can the 'step' text be actually bigger and darker
   canonical". `NavSection` prints its title at the DS sidebar spec (12px/16px,
   text-tertiary) and has no size or emphasis prop. Its title slot takes a node,
   so the step heads go in as the DS label tone: text-sm (14px), medium,
   text-primary, kept on the head's own 16px line so the DS 10px gap from the
   head to the first row holds. DS gap to report: a `NavSection` title
   emphasis prop; reported, not patched here. */
const RailHead = ({ children }: { children: ReactNode }) => (
	<span className="cg-text-text-sm cg-font-medium cg-text-text-primary cg-leading-4">{children}</span>
)

/** Page index to pane key. The pane crossfades on the key, the rail reads the index. */
const PAGE_KEYS: PageKey[] = ['details', 'email', 'crm', 'bot']

/**
 * The rail row's lead mark. The DS `TaskCheck` carries to do (an empty circle)
 * and done (the green check). R250 adds the third state in the same 16px box:
 * a minus circle in text-tertiary while the substep's switch is off. The
 * layers cross over the way TaskCheck's own two do, 200ms scale and opacity,
 * and both stay mounted, so no state arrives or leaves with a cut.
 */
const RailMark = ({ done, off }: { done: boolean; off: boolean }) => (
	<span className="relative block h-4 w-4 shrink-0">
		<span
			className={`absolute inset-0 transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none ${
				off ? 'scale-50 opacity-0' : 'scale-100 opacity-100'
			}`}
		>
			<TaskCheck done={done} />
		</span>
		<MinusCircle
			aria-hidden
			className={`absolute inset-0 h-4 w-4 cg-text-text-tertiary transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none ${
				off ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
			}`}
		/>
		<span className="sr-only">{off ? 'Off' : ''}</span>
	</span>
)

export const LinkFormCardsModal = ({
	open,
	onClose,
	onLink
}: LinkFormCardsModalProps) => {
	const { mounted, entered } = useEnterExit(open, { duration: 200 })
	const { api, setup, canWrite, crmAccounts, addCrmAccount } = useFormSpam()

	const [page, setPage] = useState<Page>(0)

	// Page 0: the form.
	const [name, setName] = useState('')
	const [url, setUrl] = useState('')
	// R262: the demo URL check lives in `useUrlVerify` now, shared with the drawer.
	const previewVerify = useUrlVerify(api ? '' : url, open)
	const [pageCheck, setPageCheck] = useState<PageCheck | null>(null)
	const [checkingPage, setCheckingPage] = useState(false)
	const [selectorName, setSelectorName] = useState('')
	const [requiresSession, setRequiresSession] = useState(false)
	const [error, setError] = useState('')
	const verify = api
		? checkingPage
			? 'checking'
			: pageCheck?.resolved && pageCheck.email_field_detected
			? 'detected'
			: pageCheck
			? 'missing'
			: 'idle'
		: previewVerify
	useEffect(() => {
		if (!api || !open || !url.trim() || requiresSession) {
			setPageCheck(null)
			setCheckingPage(false)
			return
		}
		let cancelled = false
		setCheckingPage(true)
		const timer = window.setTimeout(() => {
			void api
				.check(url, selectorName || null)
				.then((result) => {
					if (!cancelled) {
						setPageCheck(result)
						setCheckingPage(false)
					}
				})
				.catch((e) => {
					if (!cancelled) {
						setPageCheck((current) =>
							current ? { ...current, resolved: false } : null
						)
						setError(e.message || 'Unable to check this page')
						setCheckingPage(false)
					}
				})
		}, 600)
		return () => {
			cancelled = true
			window.clearTimeout(timer)
		}
	}, [api, open, url, selectorName, requiresSession])

	// Page 1: the email classes. `emailOn` is the substep switch and IS
	// `emailValidation` on the draft.
	const [emailOn, setEmailOn] = useState(true)
	const [rules, setRules] = useState<Record<string, boolean>>(emailDefaults)

	// Page 2: the CRM. `crmOn` is the substep switch; `picked` holds at most
	// ONE connection (R235: one CRM per form, a new login replaces the other).
	// R265 opened a new form on the workspace's HubSpot login. R283 (Esteban,
	// 7 Sep): "at first you should see the connectable crm options then the
	// user selects and logins to one". A new form starts with no CRM, so the
	// picker lists every CRM with Connect until a login lands.
	const [crmOn, setCrmOn] = useState(!api)
	const emptyPick = (): CrmConnections => ({ brevo: null, hubspot: null })
	const [picked, setPicked] = useState<CrmConnections>(emptyPick)
	const {
		crm,
		connectCrm,
		reset: resetCrm,
		error: crmError
	} = useCrmConnect((provider, account) => {
		const held = addCrmAccount({ provider, ...account })
		setPicked({
			brevo: provider === 'brevo' ? held.id : null,
			hubspot: provider === 'hubspot' ? held.id : null
		})
	})
	const { fieldState, checkField, resetField, verificationToken, error: fieldError } = useCrmField(api ? () => createCrmApi(api.accountId).verify(picked.hubspot || '',api.assetId) : undefined,picked.hubspot || '')
	const fieldProvider: CrmProvider | null = picked.brevo
		? 'brevo'
		: picked.hubspot
		? 'hubspot'
		: null
	// R152: one check flag, so a provider change starts it over.
	useEffect(() => {
		resetField()
		// eslint-disable-next-line
	}, [fieldProvider])
	useEffect(() => {
		if (!api?.accountId || !open) return
		let active = true
		void createCrmApi(api.accountId).preference(api.assetId).then(p => {if(active && p.connection_id && crmAccounts.some(c => c.id === p.connection_id && c.status === 'connected')) setPicked({brevo:null,hubspot:p.connection_id})}).catch(() => undefined)
		return () => {active=false}
	}, [api, open])
	const connectedAccount = fieldProvider
		? crmAccounts.find((account) => account.id === picked[fieldProvider])
		: undefined
	const connectAnother = () => {
		setPicked({ brevo: null, hubspot: null })
		resetCrm()
		resetField()
	}
	// R279 (Esteban, 4 Sep, items 8, 9): the pill's menu picks among the held
	// logins. A pick moves the draft's one connection to that login and starts
	// the field check over (R152; the `fieldProvider` effect only fires when
	// the CRM changes, so a same-CRM pick needs the explicit reset). R312: the
	// menu's Reconnect only closes the menu now, the same as Manage; the beat
	// that wrote the login back to connected is gone (see `ConnectedCrmRow`).
	const pickAccount = (account: CrmAccount) => {
		setPicked({ brevo: null, hubspot: null, [account.provider]: account.id })
		resetField()
	}

	// R323 (Gustavo, 13 Sep, item 1): which bad submission starts the block.
	const [blockThreshold, setBlockThreshold] = useState<BlockThreshold>(2)
	// Page 3: the bot switches. Two independent switches on the page, ONE
	// level and ONE trigger in the store (see the header). Block wins.
	const [botOn, setBotOn] = useState(true)
	// R322 (Gustavo, 13 Sep, item 3 of the pane list): both switches start ON.
	const [captchaOn, setCaptchaOn] = useState(true)
	const [blockOn, setBlockOn] = useState(true)
	// R278 (Esteban, 4 Sep, item 5): Normal / Strict, its own record field.
	const [sensitivity, setSensitivity] = useState<BotSensitivity>('normal')
	/* R309 (Gustavo pin #81): the CAPTCHA switch's own threshold. */
	const [captchaSensitivity, setCaptchaSensitivity] =
		useState<BotSensitivity>('normal')
	const level: BotLevel = blockOn
		? 'block'
		: captchaOn
		? 'challenge'
		: 'monitor'
	const trigger: CaptchaTrigger = blockOn ? 'high-only' : 'suspected'
	const { widgetState, checkWidget, resetWidget } = useWidgetCheck(
		api
			? async () =>
					(
						await api.verify(
							pageCheck?.resolved_url || url,
							backendSlug(name),
							selectorName || null
						)
					).verified
			: undefined,
		`${url}|${name}|${selectorName}`
	)

	// The rail's "Done" set: a substep the user left with Next and the switch on.
	const [done, setDone] = useState<Set<SubKey>>(() => new Set())
	// Step 1's check: set once the user moved on with the details in order.
	const [detailsDone, setDetailsDone] = useState(false)
	const subOn: Record<SubKey, boolean> = {
		email: emailOn,
		crm: crmOn,
		bot: botOn
	}
	// R243 (Esteban, 3 Sep): "if Flag invalid submissions on my CRM is on, you
	// should setup the fields in the crm and check for them to be able to press
	// next, if not the button should be disabled". `subOn` still drives the dim,
	// the Off badge and Skip. `subReady` is what Next, the rail's check mark and
	// Link form read: the CRM substep is ready only once a CRM is connected AND
	// the field check FOUND the field. A new CRM resets the check (R152), so the
	// check mark drops with it.
	const crmFieldReady = !!fieldProvider && fieldState === 'found'
	// R286 (Esteban, 7 Sep): "if the widget isn't verified and the blocking of
	// the bots is active, you shouldn't be able to link. you have to check the
	// page same as the other steps". The bot substep is ready only once "Check
	// my page" FOUND the widget, the same shape as the CRM field check above.
	const widgetReady = widgetState === 'found'
	// R322 (Gustavo, 13 Sep): "do not force verification before Next; enable
	// Next after Verify regardless of outcome", and the same for Finish. One
	// Verify, found OR missing, unlocks the footer. The rail's green check
	// (`subFound`) still needs a find: a substep left on a miss shows the
	// empty circle, so the record's gap stays visible.
	const crmFieldChecked =
		!!fieldProvider && (fieldState === 'found' || fieldState === 'missing')
	const widgetChecked = widgetState === 'found' || widgetState === 'missing'
	const subReady: Record<SubKey, boolean> = {
		email: emailOn,
		crm: crmOn && crmFieldChecked,
		bot: botOn && widgetChecked
	}
	const subFound: Record<SubKey, boolean> = {
		email: emailOn,
		crm: crmOn && crmFieldReady,
		bot: botOn && widgetReady
	}

	/* R337 (Gustavo, 15 Sep): "I noticed that step 3 does not get marked as
	   complete even if I verify, should we make it complete?" It could not. The
	   rail's check waits for the user to LEAVE a substep with Next, and the
	   LAST substep has no Next, only Finish, so its row stayed empty however the
	   verify went. The last row settles on its own find instead.
	   The first two rows keep the leave-with-Next rule. He saw a row he left on
	   a failed check stay empty and called that fine: "I also verified step 2
	   but clicked "next" when it failed, so it was left unchecked, Maybe that is
	   fine". A find still gates every row, so a miss never draws a check. */
	const lastSub = CARDS[CARDS.length - 1].key
	const settled = (key: SubKey) =>
		subFound[key] && (key === lastSub || done.has(key))

	const [request, setRequest] = useState<RequestState>('idle')
	const [requestName, setRequestName] = useState('')
	const requestTimer = useRef<number>()
	const createTimer = useRef<number>()
	const [creating, setCreating] = useState(false)
	// R264: Discard asks before it drops the draft. The button always asks;
	// Escape asks only once the draft holds something (the legacy rule).
	// R322 (Esteban, 14 Sep): the Discard button is gone, "Watch tutorial"
	// took its slot. The X and Escape still ask before they drop a draft.
	const [confirmDiscard, setConfirmDiscard] = useState(false)
	const dirty = url.trim().length > 0 || name.trim().length > 0 || page > 0
	const requestClose = () => {
		if (creating) return
		if (!dirty) {
			onClose()
			return
		}
		setConfirmDiscard(true)
	}

	useEffect(
		() => () => {
			window.clearTimeout(requestTimer.current)
			window.clearTimeout(createTimer.current)
		},
		[]
	)

	// A closed modal forgets everything, so the next open starts on page 0.
	useEffect(() => {
		if (open) return
		window.clearTimeout(createTimer.current)
		window.clearTimeout(requestTimer.current)
		setPage(0)
		setConfirmDiscard(false)
		setName('')
		setSelectorName('')
		setError('')
		setUrl('')
		setRequiresSession(false)
		setEmailOn(true)
		setRules(emailDefaults())
		setCrmOn(!api)
		setPicked(emptyPick())
		setBotOn(true)
		setCaptchaOn(true)
		setBlockOn(true)
		setSensitivity('normal')
		setCaptchaSensitivity('normal')
		setBlockThreshold(2)
		setDone(new Set())
		setDetailsDone(false)
		setRequest('idle')
		setRequestName('')
		setCreating(false)
		resetCrm()
		resetField()
		resetWidget()
		// eslint-disable-next-line
	}, [open])

	const sendRequest = () => {
		if (!requestName.trim()) return
		setRequest('sending')
		if (api)
			void createCrmApi(api.accountId)
				.request(requestName)
				.then(() => setRequest('sent'))
				.catch((e) => {
					setRequest('open')
					setError(e.message || 'Unable to send request')
				})
		else requestTimer.current = window.setTimeout(() => setRequest('sent'), 1400)
	}

	const selector = api ? selectorName : `#${slugify(name)}`
	// R280 (Esteban, 4 Sep, item 3): the snippet's `data-action` is the slug
	// without `#`; the record still stores `selector`.
	const action = api ? backendSlug(name) : slugify(name)
	const detailsReady =
		name.trim().length > 0 &&
		!!action &&
		(requiresSession ? !!url.trim() && !!selectorName.trim() : (api ? !!pageCheck?.resolved && !checkingPage : verify === 'detected')) &&
		(!pageCheck?.requires_identification || !!selectorName.trim())
	// R49 held a consent gate here: a hard degree needed the user's word before
	// the form could link. R277 (Esteban, 4 Sep, item 6): the box is gone, so
	// the bot page no longer holds Link form. With the switch off the form
	// links with bot verification off (assumption 1), as before.
	// R243: with the CRM switch on, the form links only once the field check
	// found the field. The rail can jump past the CRM page, so the gate sits
	// here too and not only on that page's Next.
	// R286: with the bot switch on, the form links only once the page check
	// found the widget. Off, it links with bot protection off, as before.
	// R293 (Gustavo, 4 Sep, pin #88): at least one protection must be on, or
	// the form would link with nothing behind it.
	const linkReady =
		detailsReady &&
		(emailOn || crmOn || botOn) &&
		(!crmOn || crmFieldChecked) &&
		(!botOn || requiresSession || widgetChecked) &&
		!creating

	const submit = async () => {
		if (creating || !linkReady || !canWrite) return
		setCreating(true)
		setError('')
		try {
			await onLink({
				name: name.trim(),
				url: pageCheck?.resolved_url || url.trim(),
				selector,
				settings: {
					verificationMode: requiresSession ? 'browser' : 'static',
					captcha: botOn,
					captchaEnabled: captchaOn,
					blockEnabled: blockOn,
					widget: widgetState === 'found',
					...botLevelPatch(level, trigger),
					botSensitivity: sensitivity,
					captchaSensitivity,
					blockThreshold,
					emailValidation: emailOn,
					emailChecks: {
						syntax: rules.invalid,
						domain: rules.invalid,
						disposable: rules.doNotMail,
						roleBased: rules.doNotMail
					},
					emailRules: { ...rules },
					// R262: the switch itself, so the drawer's Setup tab reads it back.
					crmFlag: crmOn,
					crmVerificationToken: verificationToken,
					crm: crmOn && fieldProvider ? picked : { brevo: null, hubspot: null }
				}
			})
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Unable to link form')
		} finally {
			setCreating(false)
		}
	}

	/* The pane crossfades on the page key: the old page leaves (150ms), then
	   the new one enters. The footer reads the PAINTED page too, so its
	   buttons change with the pane and not 150ms before it. */
	const pane = useSwapKey<PageKey>(PAGE_KEYS[page])
	const painted = PAGE_KEYS.indexOf(pane.painted) as Page
	const paintedSub: SubKey | null =
		painted === 0 ? null : (pane.painted as SubKey)
	// R249 (Esteban, 3 Sep): "add a tooltip for the button when disabled,
	// lets the user know that they need to check crm". On the CRM page the
	// footer Next stays disabled until the field check finds the spam flag,
	// and nothing said why. `nextHint` names the reason for the current
	// field state; null means Next carries no tooltip. The DS Tooltip has no
	// `disabled` prop, so the footer wraps Next only while a hint holds.
	// R322: one Verify unlocks Next, found or missing, so the missing branch
	// of the hint went with the gate that held it.
	const nextHint: { content: string; description: string } | null =
		paintedSub === 'crm' && crmOn && !crmFieldChecked
			? {
					content: !fieldProvider
						? 'Connect a CRM to continue'
						: fieldState === 'checking'
						? 'Checking your CRM…'
						: 'Verify your CRM to continue',
					description:
						'Next unlocks once you verify the spam flag field in your CRM.'
			  }
			: null
	// R286: the same for Finish on the bot page. It stays disabled until
	// "Verify" finds the widget, and the tooltip says so. R321 (Gustavo, 13
	// Sep): the button and the hints say Finish and Verify.
	// R293 (Gustavo, 4 Sep, pin #88): with every protection off there is
	// nothing to link the form to, so the button waits for one switch and
	// says so. That case comes first; with the bot switch off the R286 rule
	// cannot fire, so the two never overlap.
	const linkHint: { content: string; description: string } | null =
		painted === 3 && !emailOn && !crmOn && !botOn
			? {
					content: 'Turn on one protection to continue',
					description:
						'Finish unlocks when email validation, the CRM or bot verification is on.'
			  }
			: painted === 3 && botOn && !requiresSession && !widgetChecked
			? {
					content:
						widgetState === 'checking'
							? 'Checking your page…'
							: 'Verify your page to continue',
					description: 'Finish unlocks once you verify your page.'
			  }
			: null

	const goTo = (next: Page) => {
		if (pane.leaving || next === page) return
		setPage(next)
	}
	/* Moving FORWARD settles the page being left: page 0 once its details are
	   in order, a substep once its switch is on. Back and Skip settle nothing.
	   R236: the rail rows call this too, so a jump ahead from a filled page
	   marks it the same way Next does. */
	const forward = (to: Page) => {
		if (to > page) {
			if (page === 0) {
				if (detailsReady) setDetailsDone(true)
			} else if (paintedSub && subReady[paintedSub]) {
				const key = paintedSub
				setDone((prev) => new Set(prev).add(key))
			}
		}
		goTo(to)
	}
	const back = () => goTo(Math.max(0, page - 1) as Page)
	const skip = () => goTo(Math.min(3, page + 1) as Page)
	const next = () => forward(Math.min(3, page + 1) as Page)
	// Skip lives only on pages 1 and 2 with the switch off. It folds in and out
	// sideways (`form-spam-collapse-x`), so Back slides rather than jumps.
	const skipIO = useEnterExit(
		paintedSub !== null && painted < 3 && !subOn[paintedSub],
		{
			duration: 200
		}
	)
	/* R237: Back folds in from page 1 on, the same fold Skip uses. Discard took
	   its old slot at the left of the bar, so Back must enter and leave on its
	   own instead of a label swap. */
	const backIO = useEnterExit(painted > 0, { duration: 200 })
	const navigationRef = useRef<HTMLDivElement>(null)

	/* R186 (Esteban, 1 Sep): "this shit clipping". The pane scrolls to the very
	   top edge of the sheet, so a card ran straight into the 16px corner and the
	   close button stood on bare content. A frosted band now covers the head of
	   the scrollport: the content passes UNDER it instead of being sliced by the
	   corner. Each band is ABSENT at its own end of the run, so a pane that fits
	   carries no glass at all.

	   R189 (Esteban, 1 Sep): "it kinda takes some time to set in". It did, and
	   the delay had two parts that stacked. R186 drove the bands off a 1px
	   sentinel read by an `IntersectionObserver` at `threshold: 0`. The sentinel
	   sat behind the scrollport's own `pt-5`, so it stayed inside the root until
	   the pane had already moved 21px, and the observer then reported it a frame
	   later. On top of that the band carried `transition-opacity duration-200`.
	   The glass started 21 dead pixels late and needed a further 200ms.

	   The bands now read the scroll offset itself. Opacity is a plain ramp over
	   the first `GLASS_RAMP` pixels at each end, written straight to the node
	   inside a `requestAnimationFrame`, so the glass tracks the finger with no
	   threshold, no fade and no React render per scroll event. Do NOT put a
	   transition back on these two nodes: a transition on a scroll-linked value
	   is what made it lag. */
	const scrollRef = useRef<HTMLDivElement | null>(null)
	/* R219 (Esteban, 1 Sep): "Can this scroll bar fade in and out when used".
	   A `::-webkit-scrollbar` thumb cannot animate its opacity, so the native bar
	   goes (`cg-scrollbar-none`) and the DS `usePaintedScrollbar` paints one: it
	   shows on scroll and fades out 800ms after the last scroll event, opacity
	   only, and reduced motion swaps. The R214 band effect keeps `scrollRef`, so
	   one callback ref feeds both. `thumb.ref` is stable, so this ref is too. */
	const thumb = usePaintedScrollbar()
	const setScrollNode = useCallback(
		(node: HTMLDivElement | null) => {
			scrollRef.current = node
			thumb.ref(node)
		},
		[thumb.ref]
	)
	const topGlassRef = useRef<HTMLDivElement>(null)
	const bottomGlassRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const root = scrollRef.current
		if (!mounted || !root) return
		/* R214 (Esteban, 1 Sep): "If I change between these the scroll anchor
		   stays the same, should probably reset to the top, just because of the
		   gradient inside the container". One scrollport serves every pane, so a
		   page opened after a scroll used to start half way down, under a top
		   band that was already lit. Each painted page starts at its own top.

		   `scrollTop`, not `scrollTo({ behavior: 'smooth' })`: the pane is mid
		   crossfade here, `useSwapKey` having held the old page through its exit,
		   so the jump happens while the new content is still transparent. A
		   smooth scroll would run after the fade and read as a second animation.

		   This runs before `paint()`, so the bands read the new offset, and the
		   top band is off the moment the new page appears. */
		root.scrollTop = 0
		let frame = 0
		const paint = () => {
			frame = 0
			const top = topGlassRef.current
			const bottom = bottomGlassRef.current
			if (!top || !bottom) return
			const run = root.scrollHeight - root.clientHeight
			const above = root.scrollTop
			const below = run - above
			top.style.opacity = String(Math.min(1, Math.max(0, above / GLASS_RAMP)))
			bottom.style.opacity = String(
				Math.min(1, Math.max(0, below / GLASS_RAMP))
			)
		}
		const schedule = () => {
			if (frame) return
			frame = requestAnimationFrame(paint)
		}
		paint()
		root.addEventListener('scroll', schedule, { passive: true })
		/* The scrollport keeps its size when a fold opens, so watch the content
		   too. A taller page changes `scrollHeight`, and the bottom band must
		   come back for it. */
		const resize = new ResizeObserver(schedule)
		resize.observe(root)
		Array.from(root.children).forEach((child) => resize.observe(child))
		return () => {
			if (frame) cancelAnimationFrame(frame)
			root.removeEventListener('scroll', schedule)
			resize.disconnect()
		}
	}, [mounted, pane.painted])

	if (!mounted) return null

	/* R262: the page bodies live in `form-setup-panes.tsx`. Each pane takes its
	   slice of the draft and hands back the change; the wizard keeps the walk. */
	const detailsPage = (
		<div className="flex flex-col gap-4">
			<DetailsPane
				url={url}
				name={name}
				verify={verify}
				onUrl={(next) => {
					setUrl(next)
					setSelectorName('')
					setPageCheck(null)
					setError('')
				}}
				onName={setName}
			/>
			{api && (
				<Toggle
					size="md"
					label="This form requires a session (for example, Auth0)"
					checked={requiresSession}
					onChange={(e) => {
						setRequiresSession(e.target.checked)
						setPageCheck(null)
						setError('')
						resetWidget()
					}}
				/>
			)}
			{requiresSession && <p className="text-sm cg-text-text-secondary">
				Enter the hosted page URL without temporary query parameters. Save the form first,
				then open its normal signup flow and check the widget from Setup.
			</p>}
			{api && (requiresSession || (pageCheck &&
				(pageCheck.multiple_forms || pageCheck.requires_identification ||
					(pageCheck.resolved && !pageCheck.email_field_detected)))) && (
				<>
					<Input
						label="Form name on your page"
						size="md"
						state="default"
						value={selectorName}
						onChange={(e) => setSelectorName(e.target.value)}
						placeholder="contact"
					/>
					<p className="text-sm cg-text-text-secondary">
						Give each HTML form on a page a unique name
						attribute and link it separately.{' '}
						{pageCheck?.forms
							.map((f) => f.name)
							.filter(Boolean)
							.join(', ')}
					</p>
				</>
			)}
			{pageCheck?.resolved_url && pageCheck.resolved_url !== url && (
				<p className="text-sm cg-text-text-secondary">
					Resolved page: {pageCheck.resolved_url}
				</p>
			)}
		</div>
	)

	const emailPage = (
		<EmailPane
			on={emailOn}
			rules={rules}
			threshold={blockThreshold}
			onThreshold={setBlockThreshold}
			onChange={(next) => {
				// R292: one coupled value. The last rule off takes the switch with it.
				setEmailOn(next.on)
				setRules(next.rules)
			}}
		/>
	)

	const crmPage = (
		<div className="flex flex-col gap-4">
			{(crmError || fieldError) && <p role="alert" className="text-sm text-red-600">{crmError || fieldError}</p>}
			<fieldset disabled={api && !canWrite} className="min-w-0">
				<CrmPane
					on={crmOn}
					onToggle={setCrmOn}
					crm={crm}
					onConnect={connectCrm}
					request={request}
					onRequest={() =>
						setRequest((prev) => (prev === 'idle' ? 'open' : prev))
					}
					connected={
						fieldProvider && connectedAccount
							? { provider: fieldProvider, account: connectedAccount }
							: null
					}
					accounts={crmAccounts}
					onPick={pickAccount}
					onConnectAnother={connectAnother}
					fieldState={fieldState}
					onCheckField={checkField}
				/>
			</fieldset>
		</div>
	)

	const botPage = (
		<BotPane
			on={botOn}
			onToggle={setBotOn}
			captchaOn={captchaOn}
			blockOn={blockOn}
			/* R277 (Esteban, 4 Sep, item 6): R49's consent box is gone, so a
			   switch sets its own flag and nothing else. */
			onCaptcha={setCaptchaOn}
			onBlock={setBlockOn}
			sensitivity={sensitivity}
			onSensitivity={setSensitivity}
			captchaSensitivity={captchaSensitivity}
			onCaptchaSensitivity={setCaptchaSensitivity}
			action={action}
			widgetState={widgetState}
			deferVerification={requiresSession}
			onCheckWidget={checkWidget}
		/>
	)

	const pages: Record<PageKey, ReactNode> = {
		details: detailsPage,
		email: emailPage,
		crm: crmPage,
		bot: botPage
	}

	return (
		<>
			<Modal
				open={open}
				onClose={requestClose}
				closeOnEscape={request !== 'open' && !confirmDiscard && !creating}
				closeOnOverlayClick={false}
				size="xl"
				/* R184 (Esteban, 1 Sep): the DS `modal` radius is 20px, and he read
				   it as too round for a screen this wide. `2xl` is the next step
				   DOWN the DS scale (16px), so the corner stays on the scale and
				   still sits above the 12px cards in the rail. */
				rounded="2xl"
				/* The DS Modal ships `cg-bg-surface`, and twMerge does not know the
				   `cg-` prefix, so both classes would survive and stylesheet order
				   would pick the winner. `!` settles it. `!animate-none` drops the
				   DS slide-up, because `useEnterExit` runs the exit as well. */
				className={`!w-[740px] overflow-hidden !bg-[rgb(var(--cg-canvas))] !animate-none !shadow-[0px_20px_24px_-4px_rgba(16,24,40,0.08),0px_8px_8px_-4px_rgba(16,24,40,0.03)] transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
					entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
				}`}
			>
				{/* R299 (Esteban, 11 Sep, Gustavo pin #78 "polish the design"): "Keep
				    the left and right pane as is, with their colors and everything,
				    what I'd take from this design would be the title being on the top
				    like 'add magicpath to your...' and the X icon in the same level all
				    the way to the right". One full-width title row above the two
				    panes: the title at the left, the X at the right end, one divider
				    under it. It is `DialogHeader`, the DS `ModalHeader` + `CloseButton`
				    the confirms already run. The X takes the `requestClose` path, the
				    same as Escape. The body keeps its 520px; the modal grows by this row. */}
				<div className="border-b cg-border-border-default cg-bg-surface p-4">
					{/* R332 (Esteban, 14 Sep): "just a level smaller". `xs` = Text lg 18/24. */}
					<DialogHeader
						title="Setup how ClickGuard should protect your lead form"
						onClose={requestClose}
						size="xs"
					/>
				</div>
				{error && (
					<p role="alert" className="px-4 py-2 cg-text-error">
						{error}
					</p>
				)}
				<div className="flex h-[520px]">
					{/* The rail: the steps (R299 moved the title up). R236: the rows are
					    buttons again, as the R182 cards were. R255 (Esteban, Inspect edit):
					    rail 240, the modal stays 820, so the pane grows to 580.
					    R258 (Esteban, Inspect edit): the pane is flex-1, so its width is
					    the modal minus the rail. Pane 540 = modal 780 with the rail 240.
					    R259 (Esteban): "make it a bit tighter". Pane 500 = modal 740. */}
					<div className="flex w-[240px] shrink-0 flex-col gap-4 border-r cg-border-border-default cg-bg-surface-subtle p-4">
						{/* R236 (Esteban, 3 Sep): "no DS components to save us here?" The
						    rail is the DS sidebar pattern the settings page already runs:
						    `NavSection` per step, `NavItem` per row, the checklist's
						    `TaskCheck` as the lead (an empty radio, then the green check),
						    the Off state in the lead mark since R250 (a minus circle).
						    Step 1 is always a button. The substeps open once the details
						    are in order, the same gate as Next on page 0. */}
						<div
							className="flex flex-col gap-5"
							role="group"
							aria-label="Setup steps"
						>
							<NavSection title={<RailHead>Step 1</RailHead>}>
								<NavItem
									size="sm"
									icon={<TaskCheck done={detailsDone && detailsReady} />}
									label="Form details"
									selected={page === 0}
									onClick={() => goTo(0)}
								/>
							</NavSection>
							<NavSection
								title={<RailHead>Step 2: Protective actions</RailHead>}
							>
								{CARDS.map((card, i) => {
									const at = (i + 1) as Page
									return (
										<NavItem
											key={card.key}
											size="sm"
											icon={
												<RailMark
													done={settled(card.key)}
													off={!subOn[card.key]}
												/>
											}
											label={card.label}
											selected={page === at}
											disabled={!detailsReady}
											onClick={() => forward(at)}
										/>
									)
								})}
							</NavSection>
						</div>
						{/* R334 (Esteban, 14 Sep): "we keep the 'watch tutorial' button and
						    get rid of the 'see documentation' one". The rail's foot link (R322,
						    R331) is gone. The footer's Watch tutorial is the one door to the
						    docs page (KB article 21430), which will host the tutorial too. */}
					</div>

					{/* The pane. */}
					<div className="relative flex min-w-0 flex-1 flex-col cg-bg-surface">
						{/* R237 (Esteban, 3 Sep): "get rid of the x and maybe add a discard
						    button on the bottom left". The corner X went, Discard took the
						    left of the button bar on every page. R299 (Esteban, 11 Sep, pin
						    #78) brings the X back, in the title row at the right end; Discard
						    stays in the button bar. Escape still closes through the DS Modal. */}

						{/* R186: the wrapper exists so both frosted bands hang off the
						    SCROLLPORT and not off the pane. The pane's own bottom is the
						    button bar, which is 65px lower and the wrong edge. */}
						<div className="relative min-h-0 flex-1">
							{/* R215 (Esteban, 1 Sep, inspect edit): 16px on all four sides.
							    R219: the native bar and its stable gutter are gone, the painted
							    thumb below sits over the 16px right pad. The class name stays
							    as a selector hook; it carries no rules now. */}
							<div
								ref={setScrollNode}
								onScroll={thumb.onScroll}
								className="form-spam-pane-scroll cg-scrollbar-none h-full overflow-y-auto p-4"
							>
								<div
									key={pane.painted}
									className={
										pane.leaving ? 'form-spam-leave' : 'form-spam-panel'
									}
								>
									{pages[pane.painted]}
								</div>
							</div>

							{/* R186: the two frosted bands. Both are `pointer-events-none`,
							    and since R237 nothing floats over them, so they only
							    ever paint. Each mask takes the tint AND the blur to nothing by
							    56px, so the glass has no hard edge to give itself away.
							    Each band is ABSENT at its own end of the run: the top one is
							    gone until the pane moves, the bottom one goes when the last
							    row arrives, so a pane that fits carries no glass at all. */}
							<div
								ref={topGlassRef}
								aria-hidden="true"
								className="pointer-events-none absolute inset-x-0 top-0 z-[5] h-14 backdrop-blur-[6px]"
								style={{
									opacity: 0,
									background:
										'linear-gradient(to bottom, rgb(var(--cg-surface) / 0.78), rgb(var(--cg-surface) / 0))',
									maskImage: MASK_TOP,
									WebkitMaskImage: MASK_TOP
								}}
							/>
							<div
								ref={bottomGlassRef}
								aria-hidden="true"
								className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-9 backdrop-blur-[6px]"
								style={{
									opacity: 0,
									background:
										'linear-gradient(to top, rgb(var(--cg-surface) / 0.78), rgb(var(--cg-surface) / 0))',
									maskImage: MASK_BOTTOM,
									WebkitMaskImage: MASK_BOTTOM
								}}
							/>
							{/* R219: the painted thumb. Above the glass (z 6 over 5), so a
							    band never dims it. The colour is the R187 neutral, set in
							    `form-spam-page.css`, not the DS indigo. */}
							<div
								aria-hidden="true"
								className="form-spam-pane-thumb cg-scroll-thumb z-[6]"
								style={thumb.thumbStyle}
							/>
						</div>

						{/* The button bar. It stays pinned under the scroll area.
						    R205 (Esteban, 1 Sep): 12px, not 16px.
						    R235: page 0 = Discard + Next. Pages 1 and 2 = Back + Next,
						    plus Skip while the switch is off (Next disabled). Page 3 =
						    Back + Link form; with the bot switch off there is no page to
						    skip to, so Link form stays enabled and no Skip appears.
						    R237 (Esteban, 3 Sep): Discard sits at the LEFT on every page and
						    replaces the corner X. Back folds in at the right from page 1 on,
						    the same fold Skip uses, so the bar never cuts. */}
						<div className="flex items-center justify-between border-t cg-border-border-default p-3">
							{/* R212 (Esteban, 1 Sep): "these buttons have like less L&R
							    padding than they usually do?". They did. R205 was his own
							    inspect edit and it put `cg-px-s-8` on both, 4px under the
							    DS `md` size. The override is gone, so these pay the DS 12px
							    again, the same as every other `md` button in the product. */}
							{/* R322 (Gustavo, 13 Sep; Esteban, 14 Sep: "in place of the
							    'discard' button"): the tutorial link wears the same secondary
							    `md` button, as an anchor (`as="a"`), so it opens the docs page in
							    a new tab. R334 (Esteban, 14 Sep): it is the only door to that
							    page now, because the rail's See documentation link is gone. */}
							<AppButton
								as="a"
								href={
									setup?.tutorial_url ||
									process.env.REACT_APP_FORM_SHIELD_TUTORIAL_URL ||
									(api ? undefined : FORM_SPAM_TUTORIAL_URL)
								}
								target="_blank"
								rel="noopener noreferrer"
								variant="secondary"
								size="md"
								leftIcon={<PlayCircle />}
								label="Watch tutorial"
								disabled={creating}
							/>
							{/* Back, Skip and the primary share one flex with no gap: the
							    10px before the next button lives inside each fold, so a
							    folded button leaves no gap behind. */}
							<div
								ref={navigationRef}
								tabIndex={-1}
								role="group"
								aria-label="Form setup navigation"
								className="flex shrink-0 items-center"
							>
								{backIO.mounted && (
									<CollapsingAction entered={backIO.entered} focusFallback={navigationRef}>
										<div>
											<div className="pr-2.5">
												<AppButton
													type="button"
													variant="secondary"
													size="md"
													label="Back"
													onClick={back}
													disabled={!backIO.entered || creating}
												/>
											</div>
										</div>
									</CollapsingAction>
								)}
								{skipIO.mounted && (
									<CollapsingAction entered={skipIO.entered} focusFallback={navigationRef}>
										<div>
											<div className="pr-2.5">
												<AppButton
													type="button"
													variant="secondary"
													size="md"
													label="Skip"
													onClick={skip}
													disabled={!skipIO.entered || creating}
												/>
											</div>
										</div>
									</CollapsingAction>
								)}
								{painted === 3 && linkHint ? (
									/* R286: the R249 wrap, for Link form. The button drops its
									   pointer events and the Tooltip trigger takes the hover. */
									<Tooltip
										theme="dark"
										position="top"
										showArrow
										portal
										maxWidth={260}
										content={linkHint.content}
										description={linkHint.description}
									>
										<AppButton
											type="button"
											variant="primary"
											size="md"
											label="Finish"
											className="pointer-events-none"
											disabled
											onClick={submit}
										/>
									</Tooltip>
								) : painted === 3 ? (
									/* R204 (Esteban, 1 Sep): "this is not on hug". At rest
									   `loader="dots"` keeps an invisible sizer beside the
									   label (a 14px loader box plus the 8px `md` gap), so the
									   button stood 108px for an 86px label. The loader props
									   now ride ONLY while the link runs, the same pattern R141
									   used on the drawer's pause button. The DS gap stays a
									   report for Gustavo, not a patch. */
									<AppButton
										type="button"
										variant="primary"
										size="md"
										label="Finish"
										leftIcon={
											creating ? (
												<DotMatrix size="xs" label="Saving form" />
											) : undefined
										}
										disabled={!linkReady}
										onClick={submit}
									/>
								) : nextHint ? (
									/* R249: the DS Tooltip trigger wrapper carries the mouse
									   listeners, and a disabled control swallows hover in most
									   browsers, so the button drops its pointer events here and
									   the wrapper takes the hover. `portal` lifts the bubble
									   over the modal, the same as the field panel's help icon. */
									<Tooltip
										theme="dark"
										position="top"
										showArrow
										portal
										maxWidth={260}
										content={nextHint.content}
										description={nextHint.description}
									>
										<AppButton
											type="button"
											variant="primary"
											size="md"
											label="Next"
											className="pointer-events-none"
											disabled
											onClick={next}
										/>
									</Tooltip>
								) : (
									<AppButton
										type="button"
										variant="primary"
										size="md"
										label="Next"
										disabled={
											painted === 0
												? !detailsReady
												: !subReady[paintedSub as SubKey]
										}
										onClick={next}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</Modal>
			<DiscardDraftDialog
				open={confirmDiscard}
				onClose={() => setConfirmDiscard(false)}
				onConfirm={() => {
					setConfirmDiscard(false)
					onClose()
				}}
			/>
			<RequestSourceDialog
				open={request === 'open'}
				value={requestName}
				onChange={setRequestName}
				onClose={() => setRequest('idle')}
				onSend={sendRequest}
			/>
		</>
	)
}
